import React, { Component } from 'react'
import styled from '@emotion/styled'
import { InputLabelStyled } from 'components/minutetaker/components/form/AssigneeChipInputField'
import DraggableChipContainer from '../DraggableChipContainer'
import { MoveSectionIcon } from 'assets/icons/MoveSectionIcon'
import { ManWithTimeIcon } from 'assets/icons/ManWithTimeIcon'
import { LocationIcon } from 'assets/icons/LocationIcon'
import InlineError from '../InlineError'
import i18n from 'i18next'
import {
    ColorBaseRichBlack,
    ColorBaseGreyDark,
    AtlasStatusError,
    FontWeightRegular
} from 'assets/styles/variables'
import {
    AttendeeData,
    SectionType,
    AllContacts,
    getValidId,
    mapAttendeeForChip,
    MAX_ATTENDEE_COUNT
} from './attendeeUtils'
import { CrossSectionManager, CrossSectionMoveProps } from './crossSectionManager'
import { SearchManager, SearchManagerProps } from './searchManager'
import { InstructionText, LoaderOverlay, Spinner } from './attendeeStyledComponents'
import { MAX_ATTENDANCE_CHIPS, SECTION_TYPES, ATTENDEE_SECTIONS } from 'common/constants/constants'
import { pendoTrack } from 'common/util/functions'

const StyledMoveSectionIcon = styled(MoveSectionIcon)`
    fill: ${ColorBaseRichBlack};
    width: 16px;
    height: 16px;
`

const ChipCounterContainer = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-top: 4px;
    margin-bottom: 16px;
`

const ChipCounterText = styled.span<{ maxReached: boolean }>`
    text-align: right;
    font-size: 13px;
    font-family: 'Source Sans Pro';
    font-style: normal;
    font-weight: ${FontWeightRegular};
    color: ${({ maxReached }) => (maxReached ? AtlasStatusError : ColorBaseGreyDark)};
`

const ContainerWrapper = styled.div`
    position: relative;

    & > div:first-of-type {
        position: relative;
    }
`

export interface ThreeSectionAttendeeManagerProps {
    invitedAttendees: AttendeeData[]
    presentAttendees: AttendeeData[]
    absentAttendees: AttendeeData[]

    onInvitedAttendeesChange: (attendees: AttendeeData[]) => void
    onPresentAttendeesChange: (attendees: AttendeeData[]) => void
    onAbsentAttendeesChange: (attendees: AttendeeData[]) => void

    searchOptions?: AttendeeData[]
    allContacts?: AllContacts

    enableSearch?: boolean
    readonly?: boolean
    readonlyForCreate?: boolean
    showLabels?: boolean

    invitedLabel?: string
    presentLabel?: string
    absentLabel?: string

    onAttendeesAdd?: (attendees: AttendeeData[]) => void
    onAttendeesDelete?: (attendees: AttendeeData[]) => void
    onCrossSectionMove?: (attendee: AttendeeData, fromSection: string, toSection: string) => void

    className?: string
    containerClassName?: string
    isLoading?: boolean
    dataAnalytics?: string
}

interface State {
    inlineError: string | null
}

export class ThreeSectionAttendeeManager extends Component<
    ThreeSectionAttendeeManagerProps,
    State
> {
    state: State = {
        inlineError: null
    }

    private focusAttendeeId: string | number | null = null
    private focusRetryCount = 0
    private maxFocusRetries = 5

    private crossSectionManager: CrossSectionManager
    private searchManager: SearchManager

    constructor(props: ThreeSectionAttendeeManagerProps) {
        super(props)

        const crossSectionProps: CrossSectionMoveProps = {
            invitedAttendees: props.invitedAttendees,
            presentAttendees: props.presentAttendees,
            absentAttendees: props.absentAttendees,
            onInvitedAttendeesChange: props.onInvitedAttendeesChange,
            onPresentAttendeesChange: props.onPresentAttendeesChange,
            onAbsentAttendeesChange: props.onAbsentAttendeesChange,
            showError: (message: string) => this.showError(message)
        }

        const searchProps: SearchManagerProps = {
            allContacts: props.allContacts,
            invitedAttendees: props.invitedAttendees,
            presentAttendees: props.presentAttendees,
            absentAttendees: props.absentAttendees
        }

        this.crossSectionManager = new CrossSectionManager(crossSectionProps)
        this.searchManager = new SearchManager(searchProps)
    }

    componentDidUpdate(prevProps: ThreeSectionAttendeeManagerProps) {
        const allContactsChanged = prevProps.allContacts !== this.props.allContacts
        const attendeesChanged =
            prevProps.invitedAttendees !== this.props.invitedAttendees ||
            prevProps.presentAttendees !== this.props.presentAttendees ||
            prevProps.absentAttendees !== this.props.absentAttendees

        if (allContactsChanged || attendeesChanged) {
            this.searchManager.clearCache()

            if (allContactsChanged || attendeesChanged) {
                this.forceUpdate()
            }
        }

        this.updateManagers()
        this.handleFocusRestoration(prevProps)
    }

    private updateManagers = () => {
        const crossSectionProps: CrossSectionMoveProps = {
            invitedAttendees: this.props.invitedAttendees,
            presentAttendees: this.props.presentAttendees,
            absentAttendees: this.props.absentAttendees,
            onInvitedAttendeesChange: this.props.onInvitedAttendeesChange,
            onPresentAttendeesChange: this.props.onPresentAttendeesChange,
            onAbsentAttendeesChange: this.props.onAbsentAttendeesChange,
            showError: (message: string) => this.showError(message)
        }

        const searchProps: SearchManagerProps = {
            allContacts: this.props.allContacts,
            invitedAttendees: this.props.invitedAttendees,
            presentAttendees: this.props.presentAttendees,
            absentAttendees: this.props.absentAttendees
        }

        this.crossSectionManager.updateProps(crossSectionProps)
        this.searchManager.updateProps(searchProps)
    }

    private handleFocusRestoration = (prevProps: ThreeSectionAttendeeManagerProps) => {
        if (!this.focusAttendeeId) return

        const hasDataChanged =
            prevProps.invitedAttendees !== this.props.invitedAttendees ||
            prevProps.presentAttendees !== this.props.presentAttendees ||
            prevProps.absentAttendees !== this.props.absentAttendees

        if (hasDataChanged) {
            this.focusRetryCount = 0
            this.restoreFocus()
        }
    }

    private restoreFocus = () => {
        if (!this.focusAttendeeId) return

        // Try multiple selectors to find the chip
        const selectors = [
            `[data-attendee-id="${this.focusAttendeeId}"] [tabindex="0"]`,
            `[data-attendee-id="${this.focusAttendeeId}"]`,
            `[data-attendee-id="${this.focusAttendeeId}"] .MuiChip-root`,
            `[data-attendee-id="${this.focusAttendeeId}"] button`
        ]

        let chipElement: HTMLElement | null = null

        for (const selector of selectors) {
            chipElement = document.querySelector(selector) as HTMLElement
            if (chipElement) break
        }

        if (chipElement) {
            chipElement.focus()
            this.focusAttendeeId = null
            this.focusRetryCount = 0
        } else if (this.focusRetryCount < this.maxFocusRetries) {
            this.focusRetryCount++
            setTimeout(() => {
                this.restoreFocus()
            }, 100 * this.focusRetryCount) // Exponential backoff
        } else {
            // Final fallback - search by text content
            this.fallbackFocusRestore()
        }
    }

    private fallbackFocusRestore = () => {
        if (!this.focusAttendeeId) return

        // Find all chip containers and search by data attribute
        const allChips = document.querySelectorAll('[data-attendee-id]')
        for (let i = 0; i < allChips.length; i++) {
            const chip = allChips[i] as HTMLElement
            if (chip.getAttribute('data-attendee-id') === String(this.focusAttendeeId)) {
                const focusableElement = chip.querySelector('[tabindex="0"]') as HTMLElement
                if (focusableElement) {
                    focusableElement.focus()
                    break
                }
            }
        }

        this.focusAttendeeId = null
        this.focusRetryCount = 0
    }

    private handleTimeIconToggle = (section: SectionType, attendeeId: string | number) => {
        const attendees = this.getAttendeesForSection(section)
        const updatedAttendees = attendees.map((attendee) => {
            if (getValidId(attendee) === attendeeId) {
                return { ...attendee, isTimeLate: !attendee.isTimeLate }
            }
            return attendee
        })
        this.updateSection(section, updatedAttendees)
        this.trackAttendanceSectionEvent(section, 'toggle-time')
    }

    private handleLocationIconToggle = (section: SectionType, attendeeId: string | number) => {
        const attendees = this.getAttendeesForSection(section)
        const updatedAttendees = attendees.map((attendee) => {
            if (getValidId(attendee) === attendeeId) {
                return { ...attendee, isLocationRemote: !attendee.isLocationRemote }
            }
            return attendee
        })
        this.updateSection(section, updatedAttendees)
        this.trackAttendanceSectionEvent(section, 'toggle-location')
    }

    private moveAttendee = (section: SectionType, dragIndex: number, hoverIndex: number) => {
        const attendees = this.getAttendeesForSection(section)

        if (!attendees || dragIndex < 0 || dragIndex >= attendees.length) {
            return
        }

        if (hoverIndex < 0 || hoverIndex >= attendees.length) {
            return
        }

        const draggedItem = attendees[dragIndex]
        if (!draggedItem) {
            return
        }

        // Set focus target for restoration after same-section move
        this.focusAttendeeId = getValidId(draggedItem)

        const newOrder = [...attendees]
        newOrder.splice(dragIndex, 1)
        newOrder.splice(hoverIndex, 0, draggedItem)

        this.updateSection(section, newOrder)
        this.trackAttendanceSectionEvent(section, 'reorder')
    }

    private addAttendee = (section: SectionType, nameOrAttendee: string | AttendeeData) => {
        const attendees = this.getAttendeesForSection(section)

        if (attendees.length >= MAX_ATTENDEE_COUNT) {
            this.showError(i18n.t('INVALID_ATTENDEE_COUNT', { count: MAX_ATTENDEE_COUNT }))
            return
        }

        let newAttendee: AttendeeData

        if (typeof nameOrAttendee === 'string') {
            newAttendee = {
                id: `custom_${section}_${Date.now()}`,
                text: nameOrAttendee,
                name: nameOrAttendee,
                email: null
            }
        } else {
            newAttendee = {
                ...nameOrAttendee,
                id: nameOrAttendee.id || `custom_${section}_${Date.now()}`
            }
        }

        const newAttendees = [...attendees, newAttendee]
        this.updateSection(section, newAttendees)
        this.trackAttendanceSectionEvent(section, 'add')
    }

    private showError = (message: string) => {
        this.setState({ inlineError: message })
        setTimeout(() => this.setState({ inlineError: null }), 3000)
    }

    private removeAttendee = (section: SectionType, index: number) => {
        const attendees = this.getAttendeesForSection(section)
        const newAttendees = attendees.filter((_, i) => i !== index)
        this.updateSection(section, newAttendees)
    }

    private getAttendeesForSection = (section: SectionType): AttendeeData[] => {
        switch (section) {
            case SECTION_TYPES.INVITED:
                return this.props.invitedAttendees
            case SECTION_TYPES.PRESENT:
                return this.props.presentAttendees
            case SECTION_TYPES.ABSENT:
                return this.props.absentAttendees
            default:
                return []
        }
    }

    private updateSection = (section: SectionType, attendees: AttendeeData[]) => {
        switch (section) {
            case SECTION_TYPES.INVITED:
                this.props.onInvitedAttendeesChange(attendees)
                break
            case SECTION_TYPES.PRESENT:
                this.props.onPresentAttendeesChange(attendees)
                break
            case SECTION_TYPES.ABSENT:
                this.props.onAbsentAttendeesChange(attendees)
                break
        }
    }

    private moveAttendeeBetweenSections = (
        attendee: AttendeeData,
        fromSection: SectionType,
        toSection: SectionType
    ) => {
        const targetData = this.getAttendeesForSection(toSection)
        if (targetData.length >= MAX_ATTENDEE_COUNT) {
            this.showError(i18n.t('INVALID_ATTENDEE_COUNT', { count: MAX_ATTENDEE_COUNT }))
            return
        }

        // Set focus target for restoration after cross-section move
        this.focusAttendeeId = getValidId(attendee)

        // Reset flags when dropping into the Absent/Apologies section
        const shouldResetFlags = toSection === SECTION_TYPES.ABSENT

        const normalizedAttendee: AttendeeData = {
            ...attendee,
            isTimeLate: shouldResetFlags ? false : attendee.isTimeLate,
            isLocationRemote: shouldResetFlags ? false : attendee.isLocationRemote
        }

        const sourceData = this.getAttendeesForSection(fromSection)
        const updatedSourceData = this.filterAttendeeFromList(sourceData, attendee)

        const isForwardMove = this.isForwardMove(fromSection, toSection)

        const updatedTargetData = isForwardMove
            ? [normalizedAttendee, ...targetData]
            : [...targetData, normalizedAttendee]

        this.updateSection(fromSection, updatedSourceData)
        this.updateSection(toSection, updatedTargetData)
        this.trackAttendanceSectionEvent(toSection, 'move')
    }

    private isForwardMove = (fromSection: SectionType, toSection: SectionType): boolean => {
        const sectionOrder: SectionType[] = [
            SECTION_TYPES.PRESENT as SectionType,
            SECTION_TYPES.INVITED as SectionType,
            SECTION_TYPES.ABSENT as SectionType
        ]
        const fromIndex = sectionOrder.indexOf(fromSection)
        const toIndex = sectionOrder.indexOf(toSection)

        return toIndex > fromIndex
    }

    private filterAttendeeFromList = (
        attendees: AttendeeData[],
        attendeeToRemove: AttendeeData
    ): AttendeeData[] => {
        return attendees.filter((item) => {
            if (item.id === attendeeToRemove.id) {
                return false
            }
            const itemName = (item.name || item.text || '')
                .trim()
                .replace(/\s+/g, ' ')
                .toLowerCase()
            const attendeeName = (attendeeToRemove.name || attendeeToRemove.text || '')
                .trim()
                .replace(/\s+/g, ' ')
                .toLowerCase()
            return itemName !== attendeeName
        })
    }

    private moveFromInvitedToPresent = (attendee: AttendeeData) => {
        this.moveAttendeeBetweenSections(
            attendee,
            SECTION_TYPES.INVITED as SectionType,
            SECTION_TYPES.PRESENT as SectionType
        )
    }

    private moveFromInvitedToAbsent = (attendee: AttendeeData) => {
        this.moveAttendeeBetweenSections(
            attendee,
            SECTION_TYPES.INVITED as SectionType,
            SECTION_TYPES.ABSENT as SectionType
        )
    }

    private moveFromPresentToInvited = (attendee: AttendeeData) => {
        this.moveAttendeeBetweenSections(
            attendee,
            SECTION_TYPES.PRESENT as SectionType,
            SECTION_TYPES.INVITED as SectionType
        )
    }

    private moveFromAbsentToInvited = (attendee: AttendeeData) => {
        this.moveAttendeeBetweenSections(
            attendee,
            SECTION_TYPES.ABSENT as SectionType,
            SECTION_TYPES.INVITED as SectionType
        )
    }

    private handleCrossSectionMove = (
        attendee: AttendeeData,
        targetSection: string,
        dropPosition?: number
    ) => {
        this.crossSectionManager.handleCrossSectionMove(attendee, targetSection, dropPosition)
        this.trackAttendanceSectionEvent(targetSection, 'move')
    }

    private getSearchOptionsForSection = (section: SectionType): AttendeeData[] => {
        return this.searchManager.getSearchOptionsForSection(section)
    }

    private renderAttendeesDataSourceForThreeSections = (
        _section1Data: AttendeeData[],
        _section2Data: AttendeeData[],
        _section3Data: AttendeeData[]
    ) => {
        return this.searchManager.renderAttendeesDataSourceForThreeSections(
            _section1Data,
            _section2Data,
            _section3Data
        )
    }

    private renderInlineError = (inlineError: string | null): JSX.Element | null => {
        return inlineError ? (
            <section>
                <InlineError error={inlineError} />
            </section>
        ) : null
    }

    private renderChipCounter = (count: number): JSX.Element => {
        return (
            <ChipCounterContainer>
                <ChipCounterText maxReached={count > MAX_ATTENDANCE_CHIPS}>
                    {count}/{MAX_ATTENDANCE_CHIPS}
                </ChipCounterText>
            </ChipCounterContainer>
        )
    }

    private trackAttendanceSectionEvent = (
        section: SectionType | string,
        operation: 'add' | 'reorder' | 'move' | 'toggle-time' | 'toggle-location'
    ) => {
        const prefix = this.props.dataAnalytics ?? 'BOARDS'
        let suffix: string | undefined
        switch (section) {
            case SECTION_TYPES.PRESENT:
            case ATTENDEE_SECTIONS.PRESENT:
                suffix = 'AddPresentAttendees'
                break
            case SECTION_TYPES.INVITED:
            case ATTENDEE_SECTIONS.INVITED:
                suffix = 'AddInvitedAttendees'
                break
            case SECTION_TYPES.ABSENT:
            case ATTENDEE_SECTIONS.ABSENT:
                suffix = 'AddAbsentAttendees'
                break
            default:
                suffix = undefined
        }
        if (!suffix) return
        pendoTrack(`MM-${prefix}-${suffix}`, {
            source: 'attendance-operation',
            operation
        })
    }

    render() {
        const {
            invitedAttendees,
            presentAttendees,
            absentAttendees,
            enableSearch = true,
            readonly = false,
            readonlyForCreate = false,
            showLabels = true,
            invitedLabel = i18n.t('INVITED_ATTENDEES'),
            presentLabel = i18n.t('PRESENT'),
            absentLabel = i18n.t('ABSENT'),
            className = '',
            containerClassName = '',
            isLoading = false,
            dataAnalytics = 'BOARDS'
        } = this.props

        if (readonly) {
            return (
                <div className={className}>
                    {showLabels && (
                        <>
                            <InputLabelStyled aria-label={invitedLabel} htmlFor="Invited_Attendees">
                                {invitedLabel}
                            </InputLabelStyled>
                            <div style={{ marginBottom: '16px' }}>
                                {invitedAttendees.map((attendee) => (
                                    <span key={attendee.id} style={{ marginRight: '8px' }}>
                                        {attendee.name}
                                    </span>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            )
        }

        return (
            <div className={className}>
                <InstructionText>{i18n.t('ATTENDANCE_KEEP_TRACK')}</InstructionText>
                {showLabels && (
                    <InputLabelStyled aria-label={presentLabel} htmlFor="Present_Attendees">
                        {presentLabel}
                    </InputLabelStyled>
                )}
                <ContainerWrapper>
                    <div
                        className={containerClassName}
                        data-testid="ThreeSectionAttendeeManager_Present_AttendeesChipInput"
                        id={'ChipInputFieldContainerPresent_Attendees'}
                        role="group"
                        aria-describedby="Present_Attendees">
                        <DraggableChipContainer
                            attendees={presentAttendees.map(mapAttendeeForChip)}
                            onMove={(dragIndex, hoverIndex) =>
                                this.moveAttendee(
                                    SECTION_TYPES.PRESENT as SectionType,
                                    dragIndex,
                                    hoverIndex
                                )
                            }
                            onCrossSectionMove={this.handleCrossSectionMove}
                            onRemove={(index) =>
                                this.removeAttendee(SECTION_TYPES.PRESENT as SectionType, index)
                            }
                            onAdd={(name) =>
                                this.addAttendee(SECTION_TYPES.PRESENT as SectionType, name)
                            }
                            targetSection={ATTENDEE_SECTIONS.PRESENT}
                            icon={<StyledMoveSectionIcon title={false} />}
                            interactiveIcon={<ManWithTimeIcon />}
                            secondaryInteractiveIcon={<LocationIcon />}
                            searchOptions={this.renderAttendeesDataSourceForThreeSections(
                                presentAttendees,
                                invitedAttendees,
                                absentAttendees
                            ).map(mapAttendeeForChip)}
                            enableSearch={enableSearch}
                            crossSectionAttendees={[...invitedAttendees, ...absentAttendees].map(
                                mapAttendeeForChip
                            )}
                            onMoveToNextSection={this.moveFromPresentToInvited}
                            onTimeIconToggle={(attendeeId) =>
                                this.handleTimeIconToggle(
                                    SECTION_TYPES.PRESENT as SectionType,
                                    attendeeId
                                )
                            }
                            onLocationIconToggle={(attendeeId) =>
                                this.handleLocationIconToggle(
                                    SECTION_TYPES.PRESENT as SectionType,
                                    attendeeId
                                )
                            }
                            dataAnalytics={`MM-${dataAnalytics}-AddPresentAttendees`}
                            placeholder={i18n.t('ATTENDANCE_BOX_PLACEHOLDER')}
                            readonlyForCreate={readonlyForCreate}
                        />
                        {isLoading && (
                            <LoaderOverlay>
                                <Spinner />
                            </LoaderOverlay>
                        )}
                    </div>
                </ContainerWrapper>
                {this.renderChipCounter(presentAttendees.length)}

                {showLabels && (
                    <InputLabelStyled aria-label={invitedLabel} htmlFor="Invited_Attendees">
                        {invitedLabel}
                    </InputLabelStyled>
                )}
                <ContainerWrapper>
                    <div
                        className={containerClassName}
                        data-testid="ThreeSectionAttendeeManager_Invited_AttendeesChipInput"
                        id={'ChipInputFieldContainerInvited_Attendees'}
                        role="group"
                        aria-describedby="Invited_Attendees">
                        <DraggableChipContainer
                            attendees={invitedAttendees.map(mapAttendeeForChip)}
                            onMove={(dragIndex, hoverIndex) =>
                                this.moveAttendee(
                                    SECTION_TYPES.INVITED as SectionType,
                                    dragIndex,
                                    hoverIndex
                                )
                            }
                            onCrossSectionMove={this.handleCrossSectionMove}
                            onRemove={(index) =>
                                this.removeAttendee(SECTION_TYPES.INVITED as SectionType, index)
                            }
                            onAdd={(name) =>
                                this.addAttendee(SECTION_TYPES.INVITED as SectionType, name)
                            }
                            targetSection={ATTENDEE_SECTIONS.INVITED}
                            icon={<StyledMoveSectionIcon title={false} />}
                            interactiveIcon={<ManWithTimeIcon />}
                            secondaryInteractiveIcon={<LocationIcon />}
                            searchOptions={this.renderAttendeesDataSourceForThreeSections(
                                invitedAttendees,
                                presentAttendees,
                                absentAttendees
                            ).map(mapAttendeeForChip)}
                            enableSearch={enableSearch}
                            crossSectionAttendees={[...presentAttendees, ...absentAttendees].map(
                                mapAttendeeForChip
                            )}
                            onMoveToNextSection={this.moveFromInvitedToAbsent}
                            onMoveToPreviousSection={this.moveFromInvitedToPresent}
                            onTimeIconToggle={(attendeeId) =>
                                this.handleTimeIconToggle(
                                    SECTION_TYPES.INVITED as SectionType,
                                    attendeeId
                                )
                            }
                            onLocationIconToggle={(attendeeId) =>
                                this.handleLocationIconToggle(
                                    SECTION_TYPES.INVITED as SectionType,
                                    attendeeId
                                )
                            }
                            placeholder={i18n.t('ATTENDANCE_BOX_PLACEHOLDER')}
                            dataAnalytics={`MM-${dataAnalytics}-AddInvitedAttendees`}
                            readonlyForCreate={readonlyForCreate}
                        />
                        {isLoading && (
                            <LoaderOverlay>
                                <Spinner />
                            </LoaderOverlay>
                        )}
                    </div>
                </ContainerWrapper>
                {this.renderChipCounter(invitedAttendees.length)}

                {showLabels && (
                    <InputLabelStyled aria-label={absentLabel} htmlFor="Absent_Attendees">
                        {absentLabel}
                    </InputLabelStyled>
                )}
                <ContainerWrapper>
                    <div
                        className={containerClassName}
                        data-testid="ThreeSectionAttendeeManager_Absent_AttendeesChipInput"
                        id={'ChipInputFieldContainerAbsent_Attendees'}
                        role="group"
                        aria-describedby="Absent_Attendees">
                        <DraggableChipContainer
                            attendees={absentAttendees.map(mapAttendeeForChip)}
                            onMove={(dragIndex, hoverIndex) =>
                                this.moveAttendee(
                                    SECTION_TYPES.ABSENT as SectionType,
                                    dragIndex,
                                    hoverIndex
                                )
                            }
                            onCrossSectionMove={this.handleCrossSectionMove}
                            onRemove={(index) =>
                                this.removeAttendee(SECTION_TYPES.ABSENT as SectionType, index)
                            }
                            onAdd={(name) =>
                                this.addAttendee(SECTION_TYPES.ABSENT as SectionType, name)
                            }
                            targetSection={ATTENDEE_SECTIONS.ABSENT}
                            icon={<StyledMoveSectionIcon title={false} />}
                            searchOptions={this.renderAttendeesDataSourceForThreeSections(
                                absentAttendees,
                                invitedAttendees,
                                presentAttendees
                            ).map(mapAttendeeForChip)}
                            enableSearch={enableSearch}
                            crossSectionAttendees={[...invitedAttendees, ...presentAttendees].map(
                                mapAttendeeForChip
                            )}
                            onMoveToPreviousSection={this.moveFromAbsentToInvited}
                            onTimeIconToggle={(attendeeId) =>
                                this.handleTimeIconToggle(
                                    SECTION_TYPES.ABSENT as SectionType,
                                    attendeeId
                                )
                            }
                            onLocationIconToggle={(attendeeId) =>
                                this.handleLocationIconToggle(
                                    SECTION_TYPES.ABSENT as SectionType,
                                    attendeeId
                                )
                            }
                            dataAnalytics={`MM-${dataAnalytics}-AddAbsentAttendees`}
                            placeholder={i18n.t('ATTENDANCE_BOX_PLACEHOLDER')}
                            readonlyForCreate={readonlyForCreate}
                        />
                        {isLoading && (
                            <LoaderOverlay>
                                <Spinner />
                            </LoaderOverlay>
                        )}
                    </div>
                </ContainerWrapper>
                {this.renderChipCounter(absentAttendees.length)}

                {this.renderInlineError(this.state.inlineError)}
            </div>
        )
    }
}

export default ThreeSectionAttendeeManager
